import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Clock, Globe, MapPin, Monitor, Wifi } from 'lucide-react';

const RecentViewsModal = ({ isOpen, onClose }) => {
  const [recentViews, setRecentViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchRecentViews();
    }
  }, [isOpen]);

  const fetchRecentViews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/detailed-views`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des vues récentes');
      }

      const data = await response.json();
      setRecentViews(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
  };

  const getBrowserInfo = (userAgent) => {
    if (!userAgent || userAgent === 'Unknown') return { browser: '🌐', os: 'Unknown' };
    
    let browser = '🌐';
    let os = 'Unknown';
    
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';
    
    return { browser, os };
  };

  const getFlagEmoji = (countryCode) => {
    const flagMap = {
      'AU': '🇦🇺', 'US': '🇺🇸', 'GB': '🇬🇧', 'FR': '🇫🇷', 'DE': '🇩🇪',
      'CA': '🇨🇦', 'JP': '🇯🇵', 'IT': '🇮🇹', 'ES': '🇪🇸', 'NL': '🇳🇱',
      'CH': '🇨🇭', 'BE': '🇧🇪', 'SE': '🇸🇪', 'NO': '🇳🇴', 'DK': '🇩🇰'
    };
    return flagMap[countryCode] || '🌍';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        className="bg-slate-900 rounded-3xl border border-white/10 shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Dernières Vues</h2>
                <p className="text-purple-100 text-sm">
                  {recentViews.length} vues • Ordre chronologique (plus récent en premier)
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Chargement des vues récentes...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-400">
              <p>Erreur: {error}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentViews.map((view, index) => {
                const { browser, os } = getBrowserInfo(view.user_agent);
                const maskedIP = view.ip_address.replace(/(\d+\.\d+\.\d+\.)\d+/, '$1xxx');
                const timeAgo = getTimeAgo(view.timestamp);
                
                return (
                  <motion.div
                    key={view.id}
                    className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/8 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                  >
                    <div className="flex items-center justify-between">
                      {/* Left side - Time and Location */}
                      <div className="flex items-center space-x-4">
                        {/* Time Badge */}
                        <div className="flex flex-col items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            #{recentViews.length - index}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 whitespace-nowrap">
                            {timeAgo}
                          </div>
                        </div>

                        {/* Location Info */}
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl">{getFlagEmoji(view.country_code)}</div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-white font-medium">{view.country}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-300">{view.city}</span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <Wifi className="w-3 h-3 text-gray-500" />
                              <span className="text-gray-400 text-sm">{maskedIP}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right side - Browser and Time */}
                      <div className="flex items-center space-x-6 text-right">
                        {/* Browser Info */}
                        <div className="flex items-center space-x-2">
                          <Monitor className="w-4 h-4 text-gray-400" />
                          <div className="text-sm">
                            <div className="text-gray-300">{browser}</div>
                            <div className="text-gray-500 text-xs">{os}</div>
                          </div>
                        </div>

                        {/* Exact Time */}
                        <div className="text-sm">
                          <div className="text-gray-300 font-mono">
                            {formatDate(view.timestamp).split(' ')[1]}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatDate(view.timestamp).split(' ')[0]}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Timeline connector */}
                    {index < recentViews.length - 1 && (
                      <div className="ml-5 mt-3 mb-1 border-l-2 border-dashed border-gray-600 h-4"></div>
                    )}
                  </motion.div>
                );
              })}

              {/* Empty State */}
              {recentViews.length === 0 && (
                <div className="text-center py-12">
                  <Clock className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <h3 className="text-lg font-medium text-gray-400 mb-2">Aucune vue récente</h3>
                  <p className="text-gray-500">Les vues apparaîtront ici dès que des visiteurs arrivent sur le site.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default RecentViewsModal;